import { useContext, useState, useEffect, useRef } from "react";
import { FormContext } from "../FormContext";
import classes from "./classes.module.scss";
import classNames from "classnames";
import Typography, { ITypo, ITypoColor } from "components/elements/Typography";
import Info from "components/elements/Icons/Info";
import InputElement, { EInputType } from "../InputElement";
import NumberUtils from "utils/NumberUtils";
import ToggleSwitch from "../ToggleSwitch";
import NumberInputElement from "../NumberInputElement";

enum InputMode {
	PERCENTAGE = "percentage",
	ABSOLUTE = "absolute",
}

export type PercentageInput = {
	name: string;
	label: string;
	defaultValue?: string;
};

export type IProps = {
	totalValue: number;
	name: string;
	inputs: PercentageInput[];
	label?: string;
	className?: string;
	hidden?: boolean;
	readonly?: boolean;
	canPromptAbsoluteValues?: boolean;
};

export default function MultiplePercentagesInput(props: IProps) {
	const { name, inputs, className, hidden = false, label, readonly = false, totalValue, canPromptAbsoluteValues = false } = props;
	const context = useContext(FormContext);
	let errors = context?.getMessagesErrors(name);
	const hasErrors = errors?.length > 0;

	const [inputMode, setInputMode] = useState<InputMode>(InputMode.PERCENTAGE);
	const containerRef = useRef<HTMLDivElement>(null);

	const [values, setValues] = useState<{ [key: string]: string }>(
		inputs.reduce(
			(acc, input) => {
				acc[input.name] = input.defaultValue ?? "";
				return acc;
			},
			{} as { [key: string]: string },
		),
	);

	// Intercept form submission to convert values to percentages
	useEffect(() => {
		if (!canPromptAbsoluteValues) return;
		if (!containerRef.current) return;

		const form = containerRef.current.closest("form");
		if (!form) return;

		const handleSubmit = () => {
			if (inputMode === InputMode.ABSOLUTE) {
				// Convert all absolute values to percentages before submission
				inputs.forEach((input) => {
					const inputElement = form.querySelector(`input[name="${input.name}"]`) as HTMLInputElement;
					if (inputElement && inputElement.value) {
						// Remove all spaces from the NumberInputElement before calculation
						const absoluteValue = parseFloat(inputElement.value.replace(/\s/g, "") || "0");
						const percentageValue = totalValue > 0 ? (absoluteValue / totalValue) * 100 : 0;

						inputElement.value = percentageValue.toString();
					}
				});
			}
		};

		form.addEventListener("submit", handleSubmit);

		return () => {
			form.removeEventListener("submit", handleSubmit);
		};
	}, [canPromptAbsoluteValues, inputMode, inputs, totalValue]);

	const handleModeChange = (selected: "left" | "right") => {
		const newMode = selected === "left" ? InputMode.PERCENTAGE : InputMode.ABSOLUTE;
		setInputMode(newMode);

		// Convert current values to the new mode
		const convertedValues: { [key: string]: string } = {};
		Object.keys(values).forEach((key) => {
			const currentValue = parseFloat(values[key] || "0");
			if (newMode === InputMode.ABSOLUTE) {
				// Convert % to absolute value, rounded to 2 decimals to avoid floating point drift
				const absolute = (currentValue / 100) * totalValue;
				convertedValues[key] = (Math.round(absolute * 100) / 100).toString();
			} else {
				// Convert absolute to %, store full precision so back-conversion to € is lossless
				convertedValues[key] = totalValue > 0 ? ((currentValue / totalValue) * 100).toString() : "0";
			}
		});
		setValues(convertedValues);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setValues((prev) => ({ ...prev, [name]: value.replace(/\s/g, "") }));
	};

	// Calculate the equivalent value to show
	const getEquivalentValue = (inputName: string): string => {
		const displayValue = values[inputName];
		if (!displayValue) return "0 €";

		const numValue = parseFloat(displayValue);
		if (inputMode === InputMode.PERCENTAGE) {
			// Show euro equivalent, rounded to 2 decimals
			const euros = Math.round((numValue / 100) * totalValue * 100) / 100;
			return NumberUtils.formatNumberAddingSpaces(euros) + " €";
		} else {
			// Show percentage equivalent, truncated to 2 decimals (no rounding)
			const pct = totalValue > 0 ? Math.trunc((numValue / totalValue) * 10000) / 100 : 0;
			return NumberUtils.formatNumberAddingSpaces(pct) + " %";
		}
	};

	return (
		<div ref={containerRef} className={classNames(className, classes["root"], hidden && classes["hidden"])} id={name}>
			{label && !hidden && (
				<Typography typo={ITypo.P_LARGE_BOLD} color={ITypoColor.WILD_SAND_950} className={classes["label"]}>
					{label}
				</Typography>
			)}
			<div className={classes["inputs-container"]}>
				{hasErrors && !hidden && (
					<div className={classes["errors-container"]}>
						<div className={classes["error-icon"]}>
							<Info />
						</div>
						<div className={classes["errors"]}>
							{errors.map((message, i) => (
								<Typography typo={ITypo.CAPTION} key={i} color={ITypoColor.ERROR_800}>
									{message}
								</Typography>
							))}
						</div>
					</div>
				)}
				{!readonly && !hidden && canPromptAbsoluteValues && (
					<div className={classes["input"]}>
						<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_950}>
							Mode de saisie
						</Typography>
						<ToggleSwitch
							leftLabel="Pourcentages"
							rightLabel="Euros"
							selected={inputMode === InputMode.PERCENTAGE ? "left" : "right"}
							onChange={handleModeChange}
							disabled={readonly}
						/>
					</div>
				)}
				{inputs.map((input) => {
					return (
						<div className={classes["input"]} key={input.name}>
							{input.label && <Typography typo={ITypo.P_MEDIUM}>{input.label}</Typography>}
							<div className={classes["percentage-container"]}>
								{inputMode === InputMode.PERCENTAGE && (
									<InputElement
										key={`${input.name}-${inputMode}`}
										name={input.name}
										defaultValue={(() => {
											const raw = values[input.name] ?? input.defaultValue ?? "";
											const num = parseFloat(raw);
											return isNaN(num) ? raw : (Math.trunc(num * 100) / 100).toString();
										})()}
										type={EInputType.NUMBER}
										readonly={readonly}
										min={0}
										max={100}
										step={0.01}
										onChange={handleChange}
									/>
								)}
								{inputMode === InputMode.ABSOLUTE && (
									<NumberInputElement
										key={`${input.name}-${inputMode}`}
										name={input.name}
										defaultValue={values[input.name] ?? input.defaultValue}
										readonly={readonly}
										min={0}
										max={totalValue}
										onChange={handleChange}
									/>
								)}
								<Typography typo={ITypo.P_LARGE} color={ITypoColor.WILD_SAND_500} className={classes["percentage"]}>
									{inputMode === InputMode.PERCENTAGE ? "%" : "€"}
								</Typography>
							</div>
							≈&nbsp;&nbsp;
							{getEquivalentValue(input.name)}
						</div>
					);
				})}
			</div>
			<div className={classes["separator"]} />
		</div>
	);
}
